/**
 * The branded loader (GDD §7.1): its markup and styles live in index.html so it paints before any script runs.
 * Boot stages move the bar; `done` fades it out once the first frame is drawn.
 */

export interface Loader {
  /** Progress 0..1 (only moves forward). */
  progress(p: number): void;
  /** Fades the loader out and removes it. */
  done(): void;
}

export function attachLoader(doc: Document = document): Loader {
  const root = doc.getElementById('hw-loader');
  const fill = root?.querySelector<HTMLElement>('.hw-loader-fill') ?? null;
  let shown = 0;
  return {
    progress(p) {
      const v = Math.max(shown, Math.min(1, p));
      shown = v;
      if (fill) fill.style.transform = `scaleX(${Math.max(0.04, v).toFixed(3)})`;
      root?.setAttribute('aria-valuenow', String(Math.round(v * 100)));
    },
    done() {
      if (!root) return;
      this.progress(1);
      root.classList.add('hw-done');
      setTimeout(() => root.remove(), 600);
    },
  };
}

/**
 * Lets the browser paint (two animation frames), so the bar shows before a long synchronous stage. A hidden tab
 * runs no animation frames, so a short timer stands in.
 */
export function nextPaint(): Promise<void> {
  return new Promise((resolve) => {
    const t = setTimeout(resolve, 120);
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        clearTimeout(t);
        resolve();
      }),
    );
  });
}
